import {
  computePosition,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
} from "@floating-ui/dom";
import { gsap } from "gsap";

/**
 * Inicializa todos os dropdowns baseados em classes
 * Funciona como Bootstrap: busca elementos com classe 'dropdown-toggle' e seus menus relacionados
 */
export function initFloatingDropdowns() {
  const triggers = document.querySelectorAll(".dropdown-toggle");

  triggers.forEach((trigger) => {
    initSingleDropdown(trigger);
  });
}

/**
 * Inicializa um único dropdown
 *
 * @param {HTMLElement} triggerElement - Elemento trigger do dropdown
 */
function initSingleDropdown(triggerElement) {
  // Busca o menu relacionado (pode estar próximo no DOM ou usar data-target)
  let menuElement = null;

  // Tenta buscar por data-target
  const targetSelector = triggerElement.getAttribute("data-target");
  if (targetSelector) {
    menuElement = document.querySelector(targetSelector);
  }

  // Se não encontrou, busca o próximo elemento com classe dropdown-menu
  if (!menuElement) {
    menuElement = triggerElement.nextElementSibling?.classList.contains(
      "dropdown-menu"
    )
      ? triggerElement.nextElementSibling
      : triggerElement.parentElement?.querySelector(".dropdown-menu");
  }

  // Busca a seta dentro do menu
  const arrowElement = menuElement?.querySelector(".dropdown-arrow");

  if (!menuElement) return;

  let cleanup = null;
  let isOpen = false;

  const updatePosition = () => {
    if (!isOpen) return;

    const middleware = [
      offset(12),
      flip({
        fallbackPlacements: ["bottom-start", "top-start"],
      }),
      shift({ padding: 8 }),
    ];

    if (arrowElement) {
      middleware.push(arrow({ element: arrowElement }));
    }

    // Verifica se o trigger está dentro de um elemento fixo
    const isFixed = triggerElement.closest(".fixed") !== null;

    computePosition(triggerElement, menuElement, {
      placement: "bottom-start", // Preferência: abaixo, alinhado à esquerda
      strategy: isFixed ? "fixed" : "absolute", // Usa 'fixed' se o elemento pai for fixo
      middleware,
    }).then(({ x, y, placement: finalPlacement, middlewareData }) => {
      Object.assign(menuElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      if (arrowElement && middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;
        const placementSide = finalPlacement.split("-")[0];
        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[placementSide];

        const isVertical =
          placementSide === "top" || placementSide === "bottom";

        // Reset styles
        Object.assign(arrowElement.style, {
          left: "",
          right: "",
          top: "",
          bottom: "",
          transform: "",
        });

        if (isVertical) {
          // Para placements verticais (top/bottom), apenas x é definido (conforme documentação)
          Object.assign(arrowElement.style, {
            left: arrowX != null ? `${arrowX}px` : "",
            [staticSide]: "-12px",
          });
        } else {
          // Para placements horizontais (left/right), apenas y é definido (conforme documentação)
          Object.assign(arrowElement.style, {
            top: arrowY != null ? `${arrowY}px` : "",
            [staticSide]: "-12px",
          });
        }
      }
    });
  };

  const openMenu = () => {
    if (isOpen) return;
    isOpen = true;
    menuElement.style.display = "block";
    triggerElement.classList.add("active");
    menuElement.classList.add("active");
    updatePosition();
    cleanup = autoUpdate(triggerElement, menuElement, updatePosition);

    // Animação de abertura
    gsap.fromTo(
      menuElement,
      { opacity: 0, scale: 0.95, y: -8 },
      { opacity: 1, scale: 1, y: 0, duration: 0.2, ease: "power2.out" }
    );
  };

  const closeMenu = () => {
    if (!isOpen) return;
    isOpen = false;
    triggerElement.classList.remove("active");
    menuElement.classList.remove("active");

    // Animação de fechamento
    gsap.to(menuElement, {
      opacity: 0,
      scale: 0.95,
      y: -8,
      duration: 0.15,
      ease: "power2.in",
      onComplete: () => {
        menuElement.style.display = "none";
        if (cleanup) {
          cleanup();
          cleanup = null;
        }
      },
    });
  };

  const toggleMenu = () => {
    // Fecha outros dropdowns abertos
    document
      .querySelectorAll(".dropdown-toggle.active")
      .forEach((otherTrigger) => {
        if (otherTrigger !== triggerElement) {
          const otherMenu = otherTrigger.nextElementSibling?.classList.contains(
            "dropdown-menu"
          )
            ? otherTrigger.nextElementSibling
            : otherTrigger.parentElement?.querySelector(".dropdown-menu");
          if (otherMenu) {
            otherMenu.style.display = "none";
            otherMenu.classList.remove("active");
            otherTrigger.classList.remove("active");
          }
        }
      });

    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  triggerElement.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleMenu();
  });

  document.addEventListener("click", (e) => {
    if (!menuElement.contains(e.target) && !triggerElement.contains(e.target)) {
      closeMenu();
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isOpen) {
      closeMenu();
    }
  });

  closeMenu();
}
